import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import oidcAuth from '../services/auth/oidc-client';
import { loginSuccess, loginFailure } from '../redux/authSlice';
import { RootState } from '../redux/store';
import { buildAuthorizedSession } from '../utils/tokenRoles';
import { logoutAuthenticatedSession } from '../services/auth/logoutSession';

interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  login: (returnUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  getAccessToken: () => Promise<string | null>;
}

export const useAuth = (): UseAuthReturn => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const authCheckRef = useRef(false);

  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    console.log('🔐 [checkAuth] START');
    setIsLoading(true);
    try {
      console.log('🔐 [checkAuth] Getting user...');
      // Check if we already have a valid user in OIDC storage
      const existingUser = await oidcAuth.getUser();
      if (existingUser && !existingUser.expired) {
        console.log('🔐 [checkAuth] User exists and not expired');
        // User exists and token is not expired
        dispatch(loginSuccess(buildAuthorizedSession(existingUser)));
        console.log('🔐 [checkAuth] loginSuccess dispatched, returning true');

        return true;
      }
      
      // Try to authenticate if no valid user
      const authenticated = await oidcAuth.isAuthenticated();
      
      if (authenticated) {
        const refreshedUser = await oidcAuth.getUser();
        if (refreshedUser) {
          dispatch(loginSuccess(buildAuthorizedSession(refreshedUser)));
        }
      } else {
        dispatch(loginFailure('Not authenticated'));
      }
      
      return authenticated;
    } catch (error) {
      console.error('Auth check error:', error);
      dispatch(loginFailure('Auth check failed'));
      return false;
    } finally {
      console.log('🔐 [checkAuth] Finally - setting isLoading=false');
      setIsLoading(false);
      console.log('🔐 [checkAuth] Finally - done');
    }
  }, [dispatch]);

  useEffect(() => {
    console.log('🔐 [useAuth.useEffect] MOUNT - authCheckRef.current:', authCheckRef.current);
    if (!authCheckRef.current) {
      console.log('🔐 [useAuth.useEffect] Calling checkAuth()');
      authCheckRef.current = true;
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (returnUrl?: string): Promise<void> => {
    try {
      await oidcAuth.login(returnUrl || window.location.pathname);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await logoutAuthenticatedSession(dispatch);
  }, [dispatch]);

  const getAccessToken = async (): Promise<string | null> => {
    return oidcAuth.getAccessToken();
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    checkAuth,
    getAccessToken,
  };
};
